<template>
    <div id="app">
        <loading loader="dots" :active.sync="isLoading" :is-full-page="true"></loading>
        <page-layout :header="config.header" :logo="config.logo" :footer="config.footer">

            <template v-slot:navigation>
                <progress-tabs :current="current" :steps="pages" @selected="selectedTab" />
            </template>

            <form v-if="pages && pages[current]" ref="form" @submit.stop.prevent="" novalidate v-bind:class="{ 'was-validated': validated }">
                <form-page :page="pages[current]" @button="buttonClick" @save="executeSave($event)" :data="data" :attachments="attachments" :buttons="config.buttons" />
            </form>

            <div v-if="testSystem" role="tablist" class="mt-3">
                <b-card no-body class="mb-1">
                    <b-card-header header-tag="header" class="p-1" role="tab">
                        <b-button block href="#" v-b-toggle.accordion-1 size="sm">Data ({{Object.keys(data).length}})</b-button>
                    </b-card-header>
                    <b-collapse id="accordion-1" accordion="my-accordion" role="tabpanel">
                        <b-card-body>
                            <b-card-text>
                                <pre class="mt-2">{{data}}</pre>
                            </b-card-text>
                        </b-card-body>
                    </b-collapse>
                </b-card>
                <b-card no-body class="mb-1">
                    <b-card-header header-tag="header" class="p-1" role="tab">
                        <b-button block href="#" v-b-toggle.accordion-2 size="sm">Attachments</b-button>
                    </b-card-header>
                    <b-collapse id="accordion-2" accordion="my-accordion" role="tabpanel">
                        <b-card-body>
                            <b-card-text>
                                <pre class="mt-2">{{attachments}}</pre>
                            </b-card-text>
                        </b-card-body>
                    </b-collapse>
                </b-card>
            </div>

            <div v-if="testSystem" class="testSystem">
                <b-button id="project-list" to="/projects" title="Open project list" class="btn-small"><i class="fas fa-list"></i></b-button>
                <b-button id="project-delete" @click="deleteProject()" title="Delete" class="btn-small mx-1"><i class="far fa-trash-alt"></i></b-button>
            </div>

        </page-layout>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';

import PageLayout from '@/layouts/RegistrationLayout.vue'
import ProgressTabs from '@/components/ProgressTabs.vue'
import FormPage from '@/shared/components/forms/FormPage.vue'

import dataService from '@/services/data'
import emailService from '@/services/email'
import cluster from '@/config/om.json'

import EvaluateCondition from '@/shared/utils/EvaluateCondition'
import Field from '@/shared/utils/Field'

export default {
    name: 'MainPage',

    components: {
        PageLayout, ProgressTabs, FormPage, Loading
    },

    data() {
        return {
            testSystem: "true" == process.env.VUE_APP_TEST_SYSTEM,
            current: 0,
            validated: false,
            isLoading: false,
            data: {},
            attachments: {},
            status: "",
            singlePage: false, // if set then just this page is shown
            page: {}           // single page to show
        };
    },

    computed: {
        ...mapGetters(['config', 'cluster', 'uuid', 'api']),

        pages: function () {
            if (this.singlePage) {
                return [this.page]
            }

            if (!this.config || !this.config.statuses || !this.config.statuses[this.status] || !this.config.statuses[this.status].pages) {
                return []
            }

            return this.config.statuses[this.status].pages.filter(page => {
                if (page.visibleIf) {
                    var visible = EvaluateCondition.evaluate(page.visibleIf, this.data);

                    if (!visible && page.clearInvisibleFields) {
                        Field.clearFields(Field.getPageFieldNames(page), this.data, this.$delete);
                        Field.deleteUploads(Field.getPageUploadFieldNames(page), this.uuid, this.attachments, this.$delete);
                    }

                    return visible;
                }

                return true;
            });
        },
    },

    beforeRouteUpdate: function (to, from, next) {
        if (to.name == "main") {
            //console.log("route " + JSON.stringify(to.params))
            if (!to.params.uuid && sessionStorage.cluster == to.params.cluster) { // Set uuid from storage when cluster in url is the same as in storage
                this.setUUID(null).then(async () => { // Clear uuid to get data
                    to.params.uuid = sessionStorage.uuid;
                    this.pageChanged(to.params);
                    next();
                });

                return;
            }

            this.pageChanged(to.params);
        }

        next();
    },

    created: function () {
        var params = this.$route.params

        var referrer = document.referrer;
        var baseURL = process.env.VUE_APP_URL;

        // Remove uuid if it is in url from different site
        if (referrer && params.uuid && !referrer.startsWith(baseURL)) {
            console.log("Referrer: " + referrer);
            params.uuid = null;
            sessionStorage.clear();
            this.$router.replace({ name: 'main', params: { cluster: params.cluster } }); // redirect to page without uuid
            return;
        }

        if (params.uuid) { // Remember uuid and cluster if they are on url
            sessionStorage.uuid = params.uuid;
            sessionStorage.cluster = params.cluster;

            this.$router.replace({ name: 'main', params: { cluster: params.cluster } }); // redirect to page without uuid
            return;
        }
        else if (sessionStorage.cluster == params.cluster) { // Set uuid from storage when cluster in url is the same as in storage
            params.uuid = sessionStorage.uuid;
            //console.log("UUID from session storage: "+params.uuid)
        }
        else { // Clear uuid is storage
            sessionStorage.clear();
            sessionStorage.cluster = params.cluster;
        }

        this.pageChanged(params)
    },

    methods: {
        ...mapActions(['setCluster', 'setUUID', 'fetchLists', 'setConfig', 'setValidation']),

        setData(newData) {
            this.data = newData;
        },

        setAttachments(newAttachments) {
            this.attachments = newAttachments;
        },

        pageChanged: async function (params) { // cluster, uuid, page
            //console.log("Page changed " + JSON.stringify(params))

            var getData = false;
            this.validated = false;

            if (this.cluster != params.cluster) {
                await this.setCluster(params.cluster)
                getData = true;
            }

            if (!params.uuid) {
                getData = true;
            }
            else if (this.uuid != params.uuid) {
                await this.setUUID(params.uuid);
                getData = true;
            }

            if (params.page) {
                var pages = this.pages.length;
                if (pages != 0 && params.page > pages - 1) {
                    params.page = pages - 1
                }

                if (params.page > this.data.lastPage + 1) {
                    params.page = this.data.lastPage
                }

                if (params.page < 0) {
                    params.page = 0
                }

                this.current = parseInt(params.page);
            }
            else {
                this.current = 0;
            }

            if (getData) {
                this.load();
            }
        },

        buttonClick: async function (button) {
            if (await this.validate(button)) {
                this.confirm(button).then(() => {
                    this.save(button).then(() => {
                        var action;

                        switch (button.action) {
                            case "previous": action = this.previous(button); break;
                            case "next": action = this.next(button); break;
                            case "email": action = this.email(button); break;
                            case "check": action = this.check(button); break;
                            case "add": action = this.none(button); break;
                            case "remove": action = this.none(button); break;
                            default: action = button.action ? this.action(button) : this.none(button);
                        }

                        action.then(() => {
                            // Button action sucessfull
                            this.set(button);
                        });
                    });
                });
            }
        },

        // set button action
        set: function (button) {
            if (button.fieldName) {
                //console.log("Set action " + button.fieldName + "=" + button.fieldValue)

                var value = button.fieldValue;
                if (value === 'NOW') {
                    // set current time
                    value = this.now();
                }

                this.$set(this.data, button.fieldName, value);
            }
        },

        // No action button
        none: function () {
            return new Promise(resolve => {
                resolve();
            })
        },

        previous: function () {
            return new Promise(resolve => {
                window.scrollTo(0, 0);

                // Show previous page
                if (this.current > 0) {
                    this.selectPage(this.current - 1);
                }

                resolve();
            })
        },

        next: function () {
            return new Promise(resolve => {
                window.scrollTo(0, 0);

                // Show next page
                if (this.current < this.pages.length - 1) {
                    this.selectPage(this.current + 1);
                }

                resolve();
            })
        },

        check: function () {
            return new Promise(resolve => {
                // Generate summary
                this.isLoading = true;
                dataService.check(this.uuid).then(() => {
                    this.isLoading = false;
                    resolve();
                    window.open(this.api + "/summary/" + this.uuid);
                }).catch(error => {
                    this.isLoading = false;
                    this.showError(error.message)
                })
            })
        },

        // email button action
        email: function (button) {
            return new Promise(resolve => {
                // Send email
                //console.log("Sending email " + button.parameter)

                this.isLoading = true;

                emailService.send(button.parameter, this.cluster, this.uuid).then(() => {
                    this.isLoading = false;
                    this.showInfo("message.EmailSent")

                    resolve();
                }).catch(error => {
                    this.isLoading = false;
                    this.showError(error.message)
                })
            })
        },

        // button action executed on the server
        action: function (button) {
            return new Promise(resolve => {
                this.isLoading = true;

                dataService.action(this.cluster, this.uuid, button.action, button.parameter).then(data => {
                    this.isLoading = false;
                    switch (data.action) {
                        case "refresh": // Refresh data
                            this.load();
                            break;

                        case "show-page":
                            this.showPage(data.data);
                            break;
                    }

                    resolve()
                }).catch(error => {
                    this.isLoading = false;
                    this.showError(error.message)
                })
            })
        },

        /**
         * Show single page
         */
        showPage(page) {
            this.page = page;
            this.current = 0;
            this.singlePage = true;
        },

        validate: async function (button) {
            if (button.validation) {
                await this.setValidation(true); // Validation started

                if (this.$refs.form.checkValidity() === false) {
                    this.validated = true;

                    if (this.config.validationMessage) {
                        this.showError(this.config.validationMessage);
                    }

                    // Scroll to first invalid component
                    this.$nextTick(() => {
                        let invalid = document.querySelector('.form-control:invalid, .custom-control-input:invalid, .custom-select:invalid, .invalid');
                        if (invalid) {
                            let scrollTop = document.documentElement.scrollTop;
                            let scrollHeight = document.documentElement.scrollHeight;
                            let top = invalid.getBoundingClientRect().top + scrollTop;
                            //console.log("top="+top+", scrollTop="+scrollTop+", scrollHeight="+scrollHeight);

                            if (top < scrollTop || top > scrollTop + scrollHeight + 40) {
                                window.scrollTo(0, top - 40);
                            }
                        }

                        this.setValidation(false); // Validation finished
                    });

                    return false;
                }

                this.validated = false;
                await this.setValidation(false); // Validation finished
            }

            return true;
        },

        load: function () {
            return new Promise(resolve => {
                this.isLoading = true;
                dataService.load(this.cluster, this.uuid).then(async (data) => {
                    if (process.env.VUE_APP_CONFIG_ONLINE == 'true') {
                        await this.setConfig(data.cluster.config);
                        console.log("Config from server")
                    }
                    else {
                        await this.setConfig(cluster.config);
                        console.log("Config from om.json")
                    }

                    if (this.config.usedLists) {
                        this.fetchLists(this.config.usedLists) // Fetch used lists from server
                    }

                    await this.setData(data.master.variables.map);
                    await this.setUUID(data.master.uuid);
                    await this.setAttachments(data.attachments);
                    this.status = data.master.status;

                    sessionStorage.uuid = data.master.uuid;

                    this.isLoading = false;

                    var params = this.$route.params;
                    var pages = this.config && this.pages ? this.pages.length : 0;

                    var pageNumber = params.page
                    var lastPage = data.master.variables.map.lastPage;

                    if (lastPage) {
                        if (!pageNumber || pageNumber > lastPage) {
                            pageNumber = lastPage
                        }
                    }

                    if (!pageNumber || pageNumber < 0) {
                        pageNumber = 0;
                    }
                    else if (pageNumber > pages - 1) {
                        pageNumber = pages - 1;
                    }

                    this.current = parseInt(pageNumber);

                    //console.log("Opening page " + pageNumber + ", last page is " + lastPage)

                    //params = { cluster: this.cluster, uuid: data.master.uuid, page: pageNumber }

                    //console.log("Redirecting to " + JSON.stringify(params));

                    //this.$router.replace({ name: 'main', params: params }).catch(() => { })

                    resolve(data);
                }).catch(error => {
                    this.isLoading = false;
                    this.showError(error.message);
                })
            })
        },

        save: function (button) {
            return new Promise(resolve => {
                if (button.save || button.backgroundSave) {
                    this.executeSave(button.save).then(() => {
                        resolve()
                    });
                }
                else {
                    resolve()
                }
            })
        },

        confirm: function (button) {
            return new Promise(resolve => {
                if (button.confirmation) {
                    this.$bvModal.msgBoxConfirm(button.confirmation, { okVariant: 'danger', okTitle: 'YES', cancelTitle: 'NO' }).then(result => {
                        if (result) {
                            resolve();
                        }
                    })
                }
                else {
                    resolve();
                }
            })
        },

        deleteProject() {
            this.$bvModal.msgBoxConfirm('Are you sure to delete this project?', { id: 'delete-project-confirm', okVariant: 'danger', okTitle: 'YES', cancelTitle: 'NO' })
                .then(result => {
                    if (result) {
                        dataService.delete(this.cluster, this.uuid)
                            .then(() => {
                                this.showInfo("Project was deleted.");
                                this.$router.replace({ name: 'projects' })
                            })
                            .catch(error => {
                                this.catchError(error);
                            })
                    }
                }).catch(error => {
                    this.catchError(error);
                })
        },

        executeSave: function (showProgress) {
            return new Promise(resolve => {
                this.isLoading = showProgress; // No progress shown on background saving

                this.$set(this.data, 'lastPage', this.current);
                this.$set(this.data, 'link', window.location.href + (window.location.href.endsWith("/") ? '' : '/') + this.uuid + '/' + this.current);

                console.log("Saving data... ")

                dataService.save(this.cluster, this.uuid, this.data).then(() => {
                    this.isLoading = false;
                    resolve()
                }).catch(error => {
                    this.isLoading = false;
                    this.showError(error.message)
                })
            })
        },

        /**
         * Executed when tab is clicked
         */
        selectedTab: function (index) {
            if (index < this.current) {
                this.selectPage(index);
            }
        },

        selectPage: function (index) {
            if (index >= 0 && index < this.pages.length) {
                this.current = index;
                //this.$router.push({ name: 'main', params: { cluster: this.cluster, uuid: this.uuid, page: index } }).catch(() => { })
                window.scrollTo(0, 0);
            }
        }

    }
}
</script>

<style lang="scss">
    /* Common styles */

    .va-middle {
        vertical-align: middle;
    }

    /* test styles */

    .bb {
        border: blue 1px solid;
    }

    .br {
        border: red 1px solid;
    }

    pre {
        max-height: 300px;
        overflow: auto;
    }
</style>
